/**
 * useZynk is a custom hook that abstracts the logic of making api request to the backend.
 * at core, it uses axios to make the request.
 */

import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import React from "react";
import { useGlobalStore } from "./useGlobalStore";
import { dispatchCustomEvent } from "src/utils/utils";
import { constants } from "src/shared/constants";

type TStatus = "isIdle" | "isLoading" | "isSuccess" | "isError";

type TMthodParam<TPayload> = {
  [P in keyof TPayload]: TPayload[P];
} & {
  appendPath?: string | number | (string | number)[];
  queryParams?: Record<string, any>;
  onSuccess?: (data: any) => void;
  onError?: (err: any) => void;
  config?: AxiosRequestConfig;
};

export type TSesssionUnauthPayload = {
  err: any;
  onRefetch: <T = any>(newResponse: T) => void;
};

function enumStatus(status: TStatus) {
  const stat = {
    isIdle: false,
    isLoading: false,
    isSuccess: false,
    isError: false,
  };
  stat[status] = true;
  return stat;
}

function intercept(apiClient: AxiosInstance, session: string) {
  apiClient.interceptors.request.use((config) => {
    config.headers.Authorization = `Bearer ${session}`;
    return config;
  });

  apiClient.interceptors.response.use(
    (cfg) => cfg,
    (err) => {
      return new Promise((resolve, reject) => {
        if (err.response?.status !== 401) return reject(err);
        if (err.response?.status === 401) {
          const { customEvents } = constants;
          const onRefetch = (newResponse: any) => resolve(newResponse);
          const onError = (err: any) => reject(err);
          const eventPayload = { err, onRefetch, onError };
          dispatchCustomEvent<TSesssionUnauthPayload>(
            customEvents.sessionUnauthorized,
            eventPayload
          );
        }
      });
    }
  );
}

export function useZynk<TPayload = any, TResponse = any>(route: string) {
  const apiClient = axios.create();
  const [status, setStatus] = React.useState<TStatus>("isIdle");
  const { session } = useGlobalStore();
  if (session) intercept(apiClient, session);

  const post = async (payload?: TMthodParam<TPayload>): Promise<TResponse> => {
    setStatus("isLoading");
    if (!payload) payload = {} as TMthodParam<TPayload>;
    const { onSuccess, onError, config, ...restPayload } = payload;
    try {
      const res = await apiClient.post(route, restPayload, config);
      setStatus("isSuccess");
      if (onSuccess) onSuccess(res.data);
      return res?.data;
    } catch (err) {
      setStatus("isError");
      if (onError) onError(err);
      throw err;
    }
  };

  const get = async (payload?: TMthodParam<TPayload>): Promise<TResponse> => {
    setStatus("isLoading");
    if (!payload) payload = {} as TMthodParam<TPayload>;
    const { onSuccess, onError, appendPath, queryParams, ...restPayload } =
      payload;
    try {
      // apend path to the route
      if (typeof appendPath === "string" || typeof appendPath === "number") {
        route = `${route}/${String(appendPath)}`;
      }
      if (Array.isArray(appendPath)) {
        route = appendPath.reduce(
          (acc, curr) => `${String(acc)}/${String(curr)}`,
          route
        ) as string;
      }

      // adding query params
      if (queryParams) {
        const query = Object.entries(queryParams)
          .map(([key, value]) => `${key}=${value}`)
          .join("&");
        route = `${route}?${query}`;
      }

      const res = await apiClient.get(route, restPayload as AxiosRequestConfig);
      setStatus("isSuccess");
      if (onSuccess) onSuccess(res.data);
      return res?.data;
    } catch (err) {
      setStatus("isError");
      if (onError) onError(err);
      throw err;
    }
  };

  return { ...enumStatus(status), post, get };
}
